import React from 'react';
import Icon from '../../../components/AppIcon';
import type { TechFeature } from '../types';

const AICapabilitiesSection: React.FC = () => {
  const aiCapabilities: TechFeature[] = [
    {
      id: 'object-detection',
      title: 'Object Detection & Tracking',
      description: 'Real-time detection and tracking of multiple objects with 98.5% accuracy using YOLO v8 and custom-trained models',
      icon: 'Target',
      metrics: ['98.5% Accuracy', '60 FPS Processing', '100+ Object Classes']
    },
    {
      id: 'facial-recognition',
      title: 'Facial Recognition',
      description: 'Advanced facial recognition with liveness detection and anti-spoofing capabilities for secure authentication',
      icon: 'User',
      metrics: ['99.2% Accuracy', '< 0.1% False Positive', 'Privacy Compliant']
    },
    {
      id: 'behavior-analysis',
      title: 'Behavior Analysis',
      description: 'Intelligent behavior pattern recognition for anomaly detection and predictive analytics',
      icon: 'Activity',
      metrics: ['Real-time Analysis', 'Pattern Learning', 'Anomaly Detection']
    },
    {
      id: 'crowd-analytics',
      title: 'Crowd Analytics',
      description: 'Accurate crowd counting, density mapping, and flow analysis for large-scale events and retail spaces',
      icon: 'Users',
      metrics: ['95% Accuracy', 'Density Mapping', 'Flow Prediction']
    },
    {
      id: 'license-plate',
      title: 'License Plate Recognition',
      description: 'High-speed ANPR/ALPR with support for multiple countries and plate formats',
      icon: 'Car',
      metrics: ['99% Read Rate', '200+ Countries', '120 km/h Speed']
    },
    {
      id: 'scene-understanding',
      title: 'Scene Understanding',
      description: 'Contextual scene analysis with semantic segmentation and environmental awareness',
      icon: 'Eye',
      metrics: ['Multi-object Tracking', 'Scene Classification', 'Context Awareness']
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-background to-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            AI Capabilities
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            State-of-the-art computer vision algorithms delivering industry-leading accuracy and performance
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {aiCapabilities.map((capability) => (
            <div
              key={capability.id}
              className="bg-card rounded-lg p-6 shadow-brand hover:shadow-brand-lg transition-all duration-300 border border-border hover:border-primary/50"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Icon name={capability.icon} size={28} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-foreground mb-2">{capability.title}</h3>
                </div>
              </div>

              <p className="text-text-secondary mb-4 leading-relaxed">
                {capability.description}
              </p>

              <div className="space-y-2">
                {capability.metrics.map((metric, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={16} className="text-success" />
                    <span className="text-sm font-medium text-foreground">{metric}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-card rounded-lg p-8 shadow-brand border border-border">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold text-foreground mb-4">Model Training & Optimization</h3>
              <p className="text-text-secondary mb-4">
                Our AI models are continuously trained on diverse datasets and optimized for specific use cases, ensuring maximum accuracy and performance in real-world scenarios.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Icon name="Database" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">10M+ annotated training images</span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Zap" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">Continuous model improvement</span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Shield" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">Bias detection and mitigation</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-foreground mb-4">Edge Computing Support</h3>
              <p className="text-text-secondary mb-4">
                Deploy AI models at the edge for reduced latency and bandwidth usage, with automatic model optimization for different hardware configurations.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Icon name="Cpu" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">GPU, CPU, and NPU support</span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Gauge" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">Model quantization and pruning</span>
                </div>
                <div className="flex items-center gap-3">
                  <Icon name="Wifi" size={20} className="text-primary" />
                  <span className="text-sm text-foreground">Offline processing capability</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AICapabilitiesSection;